import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { BusinessUserCreate } from '@core/interfaces';
import { AuthenticationService, LanguageService } from '@core/services';
import { passwordsCompareAsTheSame, passwordValidator } from '@core/validators';
import { finalize, Subject, takeUntil } from 'rxjs';
import { BusinessRoutePaths } from '@core/configs/route-paths';

type ValidationErrors = Record<string, string[]>;

@Component({
  templateUrl: './business-register.component.html',
  styleUrls: ['./register.component.css']
})
export class BusinessRegisterComponent implements OnDestroy {
  private readonly defaultLanguage = 'en-US';
  private readonly unsubscribe$ = new Subject<void>();

  public formSubmitAttempt = false;
  public isSubmitting = false;
  public errorMessages: ValidationErrors = {};
  private language = localStorage.getItem('lang') || this.defaultLanguage;

  public registerForm: FormGroup = new FormGroup({
    firstName: new FormControl('', [
      Validators.required,
      Validators.maxLength(50),
      Validators.pattern("^[a-zA-Z\\s'-]+$")
    ]),
    lastName: new FormControl('', [
      Validators.required,
      Validators.maxLength(50),
      Validators.pattern("^[a-zA-Z\\s'-]+$")
    ]),
    phone: new FormControl(null, [
      Validators.required,
      Validators.pattern(/^[0-9+\-\s()]{6,20}$/)
    ]),
    email: new FormControl('', [
      Validators.required,
      Validators.email,
      Validators.maxLength(256)
    ]),
    businessName: new FormControl('', [Validators.required, Validators.maxLength(100)]),
    password: new FormControl('', [
      Validators.required,
      passwordValidator
    ]),
    confirmPassword: new FormControl('', [Validators.required]),
  }, [
    passwordsCompareAsTheSame('password', 'confirmPassword')
  ]);

  constructor(
    private authenticationService: AuthenticationService,
    private languageService: LanguageService
  ) {
    this.languageService.currentLanguage
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(language => {
        this.language = language || localStorage.getItem('lang') || this.defaultLanguage;
      });
  }

  @Input() error: string | null | undefined;

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  public submit(): void {
    if (this.isSubmitting) {
      return;
    }

    this.formSubmitAttempt = true;
    this.errorMessages = {};

    if (!this.registerForm.valid) {
      return;
    }

    this.isSubmitting = true;
    const formValues = this.registerForm.getRawValue();

    const user: BusinessUserCreate = {
      firstName: formValues.firstName,
      lastName: formValues.lastName,
      email: formValues.email,
      password: formValues.password,
      confirmPassword: formValues.confirmPassword,
      phone: formValues.phone,
      address: {
        country: '',
        state: '',
        city: '',
        zipCode: '',
        street: '',
        apartment: '',
      },
      businessName: formValues.businessName,
      culture: this.language || this.defaultLanguage,
    };

    this.authenticationService.createBusiness(user)
      .pipe(
        takeUntil(this.unsubscribe$),
        finalize(() => this.isSubmitting = false)
      )
      .subscribe({
        error: error => {
          this.errorMessages = this.extractErrorMessages(error);
        }
      });
  }

  public getServerErrors(field: string): string[] {
    return this.errorMessages[field.toLowerCase()] ?? [];
  }

  get email() { return this.registerForm.get('email'); }
  get firstName() { return this.registerForm.get('firstName'); }
  get lastName() { return this.registerForm.get('lastName'); }
  get businessName() { return this.registerForm.get('businessName'); }
  get phone() { return this.registerForm.get('phone'); }
  get password() { return this.registerForm.get('password'); }
  get confirmPassword() { return this.registerForm.get('confirmPassword'); }

  public signUpWithGoogle(): void {
    this.authenticationService.startGoogleAuth('signup', { redirectPath: BusinessRoutePaths.root });
  }

  private extractErrorMessages(error: unknown): ValidationErrors {
    const source = error instanceof HttpErrorResponse ? error.error : error;
    const messages: ValidationErrors = {};

    const pushMessage = (field: string, value: unknown): void => {
      const key = field.toLowerCase();
      const normalizedMessages = this.flattenMessages(value);

      if (normalizedMessages.length === 0) {
        return;
      }

      messages[key] = [...(messages[key] ?? []), ...normalizedMessages];
    };

    if (this.isRecord(source)) {
      if (this.isRecord(source.errors)) {
        Object.entries(source.errors).forEach(([field, value]) => pushMessage(field, value));
      }

      if (Array.isArray(source.errors)) {
        pushMessage('general', source.errors);
      }

      Object.entries(source).forEach(([field, value]) => {
        if (field === 'errors') {
          return;
        }

        if (field === 'message' || field === 'detail' || field === 'title') {
          pushMessage('general', value);
          return;
        }

        if (Array.isArray(value) || typeof value === 'string') {
          pushMessage(field, value);
        }
      });
    } else {
      pushMessage('general', source);
    }

    return messages;
  }

  private flattenMessages(value: unknown): string[] {
    if (value == null) {
      return [];
    }

    if (typeof value === 'string') {
      return value ? [value] : [];
    }

    if (Array.isArray(value)) {
      return value.flatMap(item => this.flattenMessages(item));
    }

    if (this.isRecord(value) && 'description' in value) {
      return this.flattenMessages(value.description);
    }

    return [String(value)];
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
  }
}
